import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AuthUser } from 'aws-amplify/auth';
import MatchCard from '../components/MatchCard';
import { Match, GroupStanding, MatchResult } from '../types';
import { getMatches, postPick, getStandings, getResults } from '../api';
import { FIXTURES } from '../fixtures';
import './Matches.css';

interface Props {
  user: AuthUser | null;
  picks: Record<string, 'home' | 'draw' | 'away'>;
  onPick: (matchId: string, pick: 'home' | 'draw' | 'away') => void;
}

type PickMeta = { revealed: boolean; challenged: boolean; aiChoice: string };

function leagueName(competition: string): string {
  return competition.split(' — ')[0].trim();
}

function countryFor(match: Match): { name: string; flag: string } | null {
  if (match.country && match.countryFlag) {
    return { name: match.country, flag: match.countryFlag };
  }
  return null;
}

function formatMatchDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function ResultsView({ results, loading, onRefresh }: { results: MatchResult[]; loading: boolean; onRefresh: () => void }) {
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!loading && results.length > 0) setUpdatedAt(new Date());
  }, [loading, results.length]);

  if (loading && results.length === 0) {
    return <div className="loading-state"><div className="spinner" /><p>Loading results…</p></div>;
  }
  if (results.length === 0) {
    return (
      <div className="standings-empty">
        <div className="standings-empty-icon">🎯</div>
        <h3>No results yet</h3>
        <p>Match results will appear here once games are played.</p>
      </div>
    );
  }

  // Group by local date (newest first)
  const byDate = new Map<string, MatchResult[]>();
  for (const r of results) {
    const key = formatMatchDate(r.date);
    const existing = byDate.get(key);
    if (existing) existing.push(r);
    else byDate.set(key, [r]);
  }

  return (
    <div className="results-container">
      <div className="results-toolbar">
        {updatedAt && (
          <span className="results-updated">
            Updated {updatedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <button className="results-refresh-btn" onClick={onRefresh} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </button>
      </div>
      {Array.from(byDate.entries()).map(([dateLabel, matches]) => (
        <div key={dateLabel} className="results-day">
          <div className="results-day-header">
            <span className="results-day-label">{dateLabel}</span>
            <span className="results-day-count">{matches.length} match{matches.length !== 1 ? 'es' : ''}</span>
          </div>
          <div className="results-cards">
            {matches.map(m => {
              const isDraw = !m.home.winner && !m.away.winner && m.status === 'finished';
              return (
                <div key={m.id} className={`result-card${m.status === 'live' ? ' result-card--live' : ''}`}>
                  {/* Home team */}
                  <div className={`result-team result-team--home${m.home.winner ? ' result-team--winner' : ''}`}>
                    <div className="result-team-info">
                      <span className="result-team-name">{m.home.name}</span>
                    </div>
                    {m.home.logo && <img src={m.home.logo} alt={m.home.name} className="result-team-logo" />}
                  </div>

                  {/* Score block */}
                  <div className="result-score-block">
                    <div className="result-scoreline">
                      <span className={`result-score-num${m.home.winner ? ' score--winner' : isDraw ? ' score--draw' : ''}`}>
                        {m.home.score}
                      </span>
                      <div className="result-status-badge">
                        {m.status === 'live'
                          ? <span className="badge-live">{m.statusDisplay}</span>
                          : <span className="badge-ft">{m.statusDisplay}</span>
                        }
                      </div>
                      <span className={`result-score-num${m.away.winner ? ' score--winner' : isDraw ? ' score--draw' : ''}`}>
                        {m.away.score}
                      </span>
                    </div>
                    {m.venue && <p className="result-venue">{m.venue}</p>}
                  </div>

                  {/* Away team */}
                  <div className={`result-team result-team--away${m.away.winner ? ' result-team--winner' : ''}`}>
                    {m.away.logo && <img src={m.away.logo} alt={m.away.name} className="result-team-logo" />}
                    <div className="result-team-info">
                      <span className="result-team-name">{m.away.name}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function StandingsView({ groups }: { groups: GroupStanding[] }) {
  if (groups.length === 0) {
    return (
      <div className="standings-empty">
        <div className="standings-empty-icon">🏆</div>
        <h3>Standings loading…</h3>
        <p>Live group standings will appear here. Try again in a moment.</p>
      </div>
    );
  }
  return (
    <>
      <div className="standings-legend">
        <span className="legend-item"><span className="legend-dot legend-confirmed"></span> Qualified</span>
        <span className="legend-item"><span className="legend-dot legend-advancing"></span> Advancing (provisional)</span>
        <span className="legend-item legend-note">Top 2 per group advance to Round of 32</span>
      </div>
      <div className="standings-grid">
        {groups.map(group => {
          const totalPlayed = group.teams.reduce((s, t) => s + t.played, 0);
          const hasStarted = totalPlayed > 0;

          // True if all 4 teams share identical pts + GD + GF — positions are a coin-flip
          const top2Tied =
            hasStarted &&
            group.teams[0]?.points === group.teams[1]?.points &&
            group.teams[0]?.goalDiff === group.teams[1]?.goalDiff &&
            group.teams[0]?.goalsFor === group.teams[1]?.goalsFor &&
            group.teams[1]?.points === group.teams[2]?.points &&
            group.teams[1]?.goalDiff === group.teams[2]?.goalDiff &&
            group.teams[1]?.goalsFor === group.teams[2]?.goalsFor;

          return (
            <div key={group.name} className="standings-group">
              <div className="standings-group-header">
                <h3 className="standings-group-name">{group.name}</h3>
                {top2Tied && <span className="standings-prov-badge">All level · provisional</span>}
              </div>
              <table className="standings-table">
                <thead>
                  <tr>
                    <th className="st-rank">#</th>
                    <th className="st-team">Team</th>
                    <th title="Played">MP</th>
                    <th title="Wins">W</th>
                    <th title="Draws">D</th>
                    <th title="Losses">L</th>
                    <th title="Goals For">GF</th>
                    <th title="Goals Against">GA</th>
                    <th title="Goal Difference">GD</th>
                    <th className="st-pts" title="Points">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {group.teams.map((t, i) => {
                    // Only show green zone when the group has actually started
                    const confirmed = t.qualified;           // ESPN advanced=1
                    const advancing = hasStarted && i < 2 && !confirmed;
                    const rowClass = confirmed ? 'row-confirmed' : advancing ? 'row-advancing' : '';

                    return (
                      <tr key={t.name} className={rowClass}>
                        <td className="st-rank">{i + 1}</td>
                        <td>
                          <div className="st-team">
                            {t.logo && <img src={t.logo} alt={t.name} className="st-logo" />}
                            <span className="st-name">{t.name}</span>
                          </div>
                        </td>
                        <td>{t.played}</td>
                        <td>{t.won}</td>
                        <td>{t.drawn}</td>
                        <td>{t.lost}</td>
                        <td>{t.goalsFor}</td>
                        <td>{t.goalsAgainst}</td>
                        <td className={t.goalDiff > 0 ? 'gd-pos' : t.goalDiff < 0 ? 'gd-neg' : ''}>
                          {t.goalDiff > 0 ? `+${t.goalDiff}` : t.goalDiff}
                        </td>
                        <td className="st-pts">{t.points}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default function Matches({ user, picks, onPick }: Props) {
  const [view, setView] = useState<'fixtures' | 'standings' | 'results'>('fixtures');
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [activeCountry, setActiveCountry] = useState<string>('All');
  const [activeLeague, setActiveLeague] = useState<string>('All');
  const [groups, setGroups] = useState<GroupStanding[]>([]);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [matchResults, setMatchResults] = useState<MatchResult[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  useEffect(() => {
    getMatches().then((data) => {
      setMatches(data.length > 0 ? data : FIXTURES);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (view !== 'standings' || groups.length > 0) return;
    setStandingsLoading(true);
    getStandings().then(setGroups).finally(() => setStandingsLoading(false));
  }, [view, groups.length]);

  useEffect(() => {
    if (view !== 'results') return;
    // Show spinner only on first load; silent refresh on subsequent visits
    if (matchResults.length === 0) setResultsLoading(true);
    getResults().then(setMatchResults).finally(() => setResultsLoading(false));
    // Auto-refresh every 90 seconds while tab is active (group stage has frequent updates)
    const interval = setInterval(() => {
      getResults().then(setMatchResults);
    }, 90_000);
    return () => clearInterval(interval);
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live score polling for Fixtures tab ──────────────────────────────────────
  // Runs only when at least one fixture is live; polls ESPN every 15 min, silent.
  const hasLiveMatch = matches.some(m => m.status === 'live');
  useEffect(() => {
    if (!hasLiveMatch) return;

    const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');

    const pollLiveScores = async () => {
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      try {
        const res = await fetch(
          `https://site.api.espn.com/apis/site/v2/sports/soccer/fifa.world/scoreboard?dates=${today}`
        );
        const body = await res.json() as {
          events?: Array<{
            id: string;
            status?: { type?: { name?: string } };
            competitions?: Array<{
              competitors?: Array<{ homeAway: string; team?: { displayName?: string }; score?: string; winner?: boolean }>;
            }>;
          }>;
        };

        const liveEvents = (body.events ?? []).filter(
          e => e.status?.type?.name !== 'STATUS_SCHEDULED'
        );
        if (!liveEvents.length) return;

        setMatches(prev => prev.map(m => {
          const ev = liveEvents.find(e => {
            const comp = e.competitions?.[0];
            const hn = comp?.competitors?.find(t => t.homeAway === 'home')?.team?.displayName ?? '';
            return norm(hn).includes(norm(m.homeTeam)) || norm(m.homeTeam).includes(norm(hn));
          });
          if (!ev) return m;

          const comp = ev.competitions?.[0];
          const home = comp?.competitors?.find(t => t.homeAway === 'home');
          const away = comp?.competitors?.find(t => t.homeAway === 'away');
          const statusName = ev.status?.type?.name ?? '';
          const isFinished = statusName === 'STATUS_FULL_TIME';
          const isLive = !isFinished && statusName !== 'STATUS_SCHEDULED';

          return {
            ...m,
            status: (isFinished ? 'finished' : isLive ? 'live' : m.status) as Match['status'],
            score: home && away ? {
              home: parseInt(home.score ?? '0') || 0,
              away: parseInt(away.score ?? '0') || 0,
            } : m.score,
          };
        }));
      } catch {
        // silent — don't surface ESPN errors to the user
      }
    };

    pollLiveScores();
    const interval = setInterval(pollLiveScores, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [hasLiveMatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countries derived from matches, sorted by match count
  const countries = useMemo(() => {
    const countMap = new Map<string, { flag: string; count: number }>();
    for (const m of matches) {
      const c = countryFor(m);
      if (!c) continue;
      const existing = countMap.get(c.name);
      if (existing) existing.count++;
      else countMap.set(c.name, { flag: c.flag, count: 1 });
    }
    return Array.from(countMap.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .map(([name, { flag }]) => ({ name, flag }));
  }, [matches]);

  // Leagues filtered to active country, sorted by match count
  const leagues = useMemo(() => {
    const source = activeCountry === 'All'
      ? matches
      : matches.filter(m => countryFor(m)?.name === activeCountry);
    const counts = new Map<string, number>();
    for (const m of source) {
      const name = leagueName(m.competition);
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [matches, activeCountry]);

  // Final match list: country filter → league filter
  const filtered = useMemo(() => {
    let result = matches;
    if (activeCountry !== 'All')
      result = result.filter(m => countryFor(m)?.name === activeCountry);
    if (activeLeague !== 'All')
      result = result.filter(m => leagueName(m.competition) === activeLeague);
    return result;
  }, [matches, activeCountry, activeLeague]);

  const handleCountrySelect = (country: string) => {
    setActiveCountry(country);
    setActiveLeague('All'); // reset league when switching country
  };

  const handlePick = useCallback(
    async (matchId: string, pick: 'home' | 'draw' | 'away', meta?: PickMeta) => {
      if (submitting || !user) return;
      setSubmitting(matchId);
      onPick(matchId, pick);
      const match = matches.find(m => m.matchId === matchId);
      if (match) {
        postPick(matchId, pick, {
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          competition: match.competition,
          kickoffTime: match.kickoffTime,
          homeTeamLogo: match.homeTeamLogo,
          awayTeamLogo: match.awayTeamLogo,
          ...(meta ?? {}),
        }).catch(console.warn);
      }
      setSubmitting(null);
    },
    [submitting, user, matches, onPick]
  );

  if (loading) {
    return (
      <div className="matches-page container">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading fixtures…</p>
        </div>
      </div>
    );
  }

  const subtitle = [
    `${filtered.length} fixture${filtered.length !== 1 ? 's' : ''}`,
    activeCountry !== 'All' ? activeCountry : `${countries.length} countr${countries.length !== 1 ? 'ies' : 'y'}`,
    activeLeague !== 'All' ? activeLeague : null,
    'AI-powered predictions',
  ].filter(Boolean).join(' · ');

  const pageSub =
    view === 'fixtures' ? subtitle :
    view === 'standings' ? 'Group stage points table' :
    `${matchResults.length} completed match${matchResults.length !== 1 ? 'es' : ''}`;

  return (
    <div className="matches-page container">
      <div className="matches-header">
        <h1 className="page-title">FIFA World Cup 2026</h1>
        <p className="page-sub">{pageSub}</p>
      </div>

      {/* Sub-tab toggle */}
      <div className="matches-tabs">
        <button
          className={`matches-tab${view === 'fixtures' ? ' active' : ''}`}
          onClick={() => setView('fixtures')}
        >
          Fixtures
        </button>
        <button
          className={`matches-tab${view === 'results' ? ' active' : ''}`}
          onClick={() => setView('results')}
        >
          Results
        </button>
        <button
          className={`matches-tab${view === 'standings' ? ' active' : ''}`}
          onClick={() => setView('standings')}
        >
          Points Table
        </button>
      </div>

      {/* Results view */}
      {view === 'results' && (
        <ResultsView
          results={matchResults}
          loading={resultsLoading}
          onRefresh={() => {
            setResultsLoading(true);
            getResults().then(setMatchResults).finally(() => setResultsLoading(false));
          }}
        />
      )}

      {/* Standings view */}
      {view === 'standings' && (
        standingsLoading
          ? <div className="loading-state"><div className="spinner" /><p>Loading standings…</p></div>
          : <StandingsView groups={groups} />
      )}

      {/* Fixtures view */}
      {view === 'fixtures' && <>

      {/* Country filter row */}
      {countries.length >= 1 && (
        <div className="country-filter">
          <button
            className={`country-pill${activeCountry === 'All' ? ' active' : ''}`}
            onClick={() => handleCountrySelect('All')}
          >
            🌍 All
          </button>
          {countries.map(({ name, flag }) => (
            <button
              key={name}
              className={`country-pill${activeCountry === name ? ' active' : ''}`}
              onClick={() => handleCountrySelect(name)}
            >
              {flag} {name}
            </button>
          ))}
        </div>
      )}

      {/* League filter row */}
      {leagues.length > 1 && (
        <div className="league-filter">
          <button
            className={`league-pill${activeLeague === 'All' ? ' active' : ''}`}
            onClick={() => setActiveLeague('All')}
          >
            All Leagues
          </button>
          {leagues.map(name => (
            <button
              key={name}
              className={`league-pill${activeLeague === name ? ' active' : ''}`}
              onClick={() => setActiveLeague(name)}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {matches.length === 0 ? (
        <div className="matches-empty">
          <div className="matches-empty-icon">📅</div>
          <h2>No upcoming fixtures</h2>
          <p>There are no scheduled matches in the next 7 days for the supported leagues. Check back soon.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="matches-empty">
          <div className="matches-empty-icon">🔍</div>
          <h2>No matches in this filter</h2>
          <p>Try selecting a different country or league.</p>
        </div>
      ) : (
        <>
          <div className="matches-grid">
            {(user ? filtered : filtered.slice(0, 4)).map((match) => (
              <MatchCard
                key={match.matchId}
                match={match}
                userPick={picks[match.matchId]}
                onPick={handlePick}
                loading={submitting === match.matchId}
                isGuest={!user}
              />
            ))}
          </div>
          {!user && filtered.length > 4 && (
            <div className="matches-guest-wall">
              <div className="matches-guest-blur">
                {filtered.slice(4, 8).map((match) => (
                  <MatchCard
                    key={match.matchId}
                    match={match}
                    userPick={undefined}
                    onPick={handlePick}
                    loading={false}
                    isGuest={true}
                  />
                ))}
              </div>
              <div className="matches-guest-overlay">
                <div className="matches-guest-cta">
                  <span className="matches-guest-cta-icon">🔒</span>
                  <h3>{filtered.length - 4} more fixture{filtered.length - 4 !== 1 ? 's' : ''} available</h3>
                  <p>Sign in free to unlock all predictions and place your picks</p>
                  <Link to="/login" className="btn-primary">Sign In Free →</Link>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </>}
    </div>
  );
}
